import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import axios from "axios";
import Select from "../../components/select";
import TagSelect from "../../components/tagSelect";
import { toast } from "react-toastify";

import { uploadMediaFile } from "../../utills/fileUpload";
import Button from '../../components/button';
import { convertToCloudFrontUrl, findSubPillarOption } from "../../helper";
import TaxonomyStatus from "../../components/taxonomyStatus";
import PublishResult from "../../components/publishResult";
import { newIdempotencyKey } from "../../helper/idempotency";
import { apiErrorMessage, apiBodyMessage, isTimeout } from "../../helper/apiError";


const PostPopup = (props) => {
  const { 
    isAddPostModalOpen, 
    isEditPostModalOpen, 
    handleCloseModal, 
    postData = {},
    adminInfo,
    onTagCreated,
    tagOptions,
    subPillarOptions = [],
    taxonomyState = "ready",
    onRetryTaxonomy,
    onSuccess,
  } = props;

  const initialPostValues = {
    title: "",
    // Posts are always NonFiction: the Category Type picker is gone from the
    // form, so the type is fixed here and on edit rather than chosen.
    type: "NonFiction",
    tags: [],
    // Only the sub-pillar is chosen; the backend resolves its parent pillar.
    subPillarId: "",
    image: null,
    video: null,
    mediaType: "",
    description: ""
  };
  const [values, setValues] = useState(initialPostValues);
  const [editMediaError, setEditMediaError] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  // Submit waits for an upload: pressed mid-upload, it saved the post with
  // the previous file (or none).
  const [isUploading, setIsUploading] = useState(false);
  const [published, setPublished] = useState(null);
  // Same rule as the guide form: one key per post being added, kept across a
  // retried Submit, so a timeout can't produce two posts.
  const idempotencyKey = useRef(null);
  useEffect(() => {
    if (isAddPostModalOpen && !isEditPostModalOpen && !idempotencyKey.current) {
      idempotencyKey.current = newIdempotencyKey();
    }
  }, [isAddPostModalOpen, isEditPostModalOpen]);
  const taxonomyReady = taxonomyState === "ready";

  useEffect(() => {
    if (isEditPostModalOpen === true && postData) {
      // Load existing tags. Older posts saved before tags fall back to their
      // genres so the picker isn't empty on edit.
      let tagData = [];
      if (Array.isArray(postData.tags) && postData.tags.length > 0) {
        tagData = postData.tags.map((t) => ({ label: t.tagName, value: t.tagId }));
      } else if (Array.isArray(postData.category)) {
        tagData = postData.category.map((c) => ({ label: c.label, value: c.value }));
      }

      setValues(prevValues => ({
        ...prevValues,
        title: postData.title || "",
        type: "NonFiction",
        tags: tagData,
        subPillarId: postData.subPillar?.subPillarId || "",
        image: postData.posts || null,
        video: postData.reels || null,
        mediaType: postData.mediaType || "image",
        description: postData.description || ""
      }));
    } else if (!isEditPostModalOpen && !isAddPostModalOpen) {
      // Reset form when modal is closed
      setValues(initialPostValues);
      setErrors({});
      setEditMediaError("");
    }
  }, [isEditPostModalOpen, postData, isAddPostModalOpen]);


  // A sub-pillar that has since been deleted is no longer in the options, but
  // the post is still tagged with it. Fall back to the name stored on the
  // post so it displays and survives an unrelated edit — clearing it here
  // would strip the classification off every old post the moment someone
  // fixed a typo. The backend resolves deleted ids for exactly this reason.
  const storedSubPillar = postData?.subPillar?.subPillarId
    ? {
        value: postData.subPillar.subPillarId,
        label: postData.subPillar.subPillarName || "(removed sub pillar)",
      }
    : null;

  const selectedSubPillar =
    findSubPillarOption(subPillarOptions, values.subPillarId) ||
    (storedSubPillar && storedSubPillar.value === values.subPillarId
      ? storedSubPillar
      : null);

  // Only a genuinely unrecognisable id gets dropped — one the options don't
  // know AND the post doesn't carry. Guarded on options being loaded, so a
  // slow or failed fetch never wipes a good value.
  useEffect(() => {
    if (subPillarOptions.length === 0 || !values.subPillarId) return;
    if (!findSubPillarOption(subPillarOptions, values.subPillarId) &&
        storedSubPillar?.value !== values.subPillarId) {
      setValues((prev) => ({ ...prev, subPillarId: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPillarOptions, values.subPillarId]);

  const handleUploadMedia = async (event) => {
    if (!event?.target?.files?.length) return;
    setIsUploading(true);
    try {
      const result = await uploadMediaFile(event, true);

      if (typeof result === "string") {
        setEditMediaError(result);
        toast.error(result);
      } else if (typeof result === "object" && result.length > 0) {
        const { mediaType, ...uploadedFile } = result[0]?.data || {};
        if (mediaType) {
          setEditMediaError("");
          setErrors((prev) => ({ ...prev, image: undefined, video: undefined, media: undefined }));
          setValues((prev) => ({
            ...prev,
            [mediaType.startsWith("image") ? "image" : "video"]: uploadedFile,
            mediaType,
          }));
        } else {
          toast.error("The upload finished but the server sent no file back. Please try again.");
        }
      } else {
        toast.error("Nothing was uploaded. Please try again.");
      }
    } catch (error) {
      console.error("PostPopup handleUploadMedia catch error::", error);
      toast.error(apiErrorMessage(error, "The file could not be uploaded."));
    } finally {
      setIsUploading(false);
    }
  };

  const handleUserValuesUpdate = ({ name, value }) => {
    // Tags aren't Fiction/NonFiction-scoped, so changing the type no longer
    // clears the selection (genres used to).
    setValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  const handleClose = () => {
    idempotencyKey.current = null;
    handleCloseModal();
    setValues(initialPostValues);
    setErrors({});
    setEditMediaError("");
  };

  const validateFields = (value) => {
    let newErrors = {};

    if (!value.tags || value.tags.length === 0) {
      newErrors.tags = "Please add at least one tag";
    }
    // if (!value.description || value.description.trim() === "") {
    //   newErrors.description = "Please add the description";
    // }
    if (!value.type || value.type.trim() === "") {
      newErrors.type = "Please add a type";
    }
    
    if (value.mediaType?.startsWith("image")) {
      if (!value.image || !value.image.uploadedImageUrl) {
        newErrors.image = "Please upload an image";
      }
    } else if (value.mediaType?.startsWith("video")) {
      if (!value.video || !value.video.uploadedImageUrl) {
        newErrors.video = "Video is required";
      }
    } else {
      newErrors.media = "Image or a video is required";
    }

    return newErrors;
  };

  const handleAddAndUpdateValues = async () => {
    if (isSubmit) return;
    if (isUploading) {
      toast.info("Wait for the upload to finish.");
      return;
    }
    if (!taxonomyReady) {
      toast.error("Pillars and tags haven't loaded — press Retry first.");
      return;
    }
    const newErrors = validateFields(values);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(Object.values(newErrors)[0]);
      return;
    }
    if (isEditPostModalOpen && !postData?._id) {
      toast.error("This post has no id, so it can't be updated. Close and open it again.");
      return;
    }

    try {
      setIsSubmit(true);
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const url = isEditPostModalOpen 
      ? `${process.env.REACT_APP_API_ROOT}/api/admin/post/update/${postData._id}` 
      : `${process.env.REACT_APP_API_ROOT}/api/admin/post/add`;
      const method = isEditPostModalOpen ? "put" : "post";
      // The key goes in the body, not a header: a custom header needs the
      // API's CORS to allow it, and the body works against any backend version.
      const body = !isEditPostModalOpen && idempotencyKey.current ? { ...values, idempotencyKey: idempotencyKey.current } : values;
      const response = await axios[method](url, { values: body }, { headers, timeout: 60000 });

      if (response?.data?.status) {
        const wasEdit = isEditPostModalOpen;
        setErrors({});
        onSuccess();
        handleCloseModal();
        setValues(initialPostValues);
        if (wasEdit) {
          toast.success("Post updated successfully.");
        } else {
          idempotencyKey.current = null;
          if (response.data.data?._id) setPublished({ post: response.data.data, duplicate: Boolean(response.data.duplicate) });
          else toast.success("Post added successfully.");
        }
      } else {
        toast.error(apiBodyMessage(response?.data) || "The post was not saved.");
      }
    } catch (error) {
      console.error("Error:", error);
      const timedOut = isTimeout(error);
      toast.error(
        timedOut
          ? "No answer from the server. Press Submit again — it won't create a second post."
          : apiErrorMessage(error, "The post was not saved.")
      );
    } finally {
      setIsSubmit(false);
    }
  };


  return (
    <>
    {published && (
      <PublishResult post={published.post} duplicate={published.duplicate} onClose={() => setPublished(null)} />
    )}
    <Modal
      isOpen={isAddPostModalOpen || isEditPostModalOpen}
      onRequestClose={isSubmit ? undefined : handleClose}
      className="modal_body_lg"
      overlayClassName="modal_overlay"
    >
      <div className="box_wrapper">
        <h4 className="text-center">{isEditPostModalOpen ? "Edit Post" : "Add Post"}</h4>
        <TaxonomyStatus state={taxonomyState} onRetry={onRetryTaxonomy} />
        {/* Matches the guide dialog: _sm caps at 800px, which left most of an
          80%-wide dialog empty. */}
        <div className="modal_content_lg">
          <div className="mb-3">
            <label className="label">Title:</label>
            <input
              className="form-control form-control-sm px-3 py-2 fw-medium"
              type="text"
              name="title"
              placeholder="Short title to find this post by..."
              value={values.title || ""}
              onChange={(e) => handleUserValuesUpdate({ name: e.target.name, value: e.target.value })}
            />
            {errors.title && <p className="error-message d-block">{errors.title}</p>}
          </div>
          <div className="mb-3">
            <label className="label">Sub Pillar:</label>
            <Select
              name="subPillarId"
              placeholder="Select a sub pillar..."
              isClearable
              value={selectedSubPillar}
              onChange={(selected) => handleUserValuesUpdate({
                name: "subPillarId",
                value: selected?.value || ""
              })}
              options={subPillarOptions}
            />
            <small className="text-muted">
              Grouped by pillar — picking a sub pillar files the post under its pillar.
            </small>
          </div>
          <div className="mb-3">
            <label className="label">Tags:</label>
            <TagSelect
                value={values.tags || []}
                onChange={(selected) => handleUserValuesUpdate({
                  name: "tags",
                  value: (selected || []).map((option) => ({ label: option.label, value: option.value })),
                })}
                options={tagOptions || []}
                adminInfo={adminInfo}
                onCreated={onTagCreated}
            />
            {errors.tags && <p className="error-message">{errors.tags}</p>}
          </div>
          <div className="mb-3">
            <label className="label">Description:</label>
            <textarea
              className="form-control form-control-sm px-3 py-2 fw-medium"
              placeholder="Write a short description about post..."
              type="text"
              name="description"
              value={values.description}
              onChange={(e) => handleUserValuesUpdate({ name: e.target.name, value: e.target.value })}
              style={{ height:"120px" }}
            />
            {/* {errors && errors.description && <p className="error-message d-block">{errors.description}</p>} */}
          </div>
          <div className="mb-3">
            <label className="label">Upload Media:</label>
            <input
              className="file"
              type="file"
              // accept=".png, .jpg, .jpeg, .webp, .svg, .gif"
              multiple
              onChange={handleUploadMedia}
              onClick={(e) => { e.target.value = null }}
            />
            {isUploading && <p className="text-muted small mb-0">Uploading…</p>}
            {editMediaError && <p className="error-message">{editMediaError}</p>}
            {errors.image && <p className="error-message">{errors.image}</p>}
            {errors.video && <p className="error-message">{errors.video}</p>}
            {errors.media && <p className="error-message">{errors.media}</p>}
          </div>
          {values.image && values.image.uploadedImageUrl && (
            <img
              src={convertToCloudFrontUrl(values.image.uploadedImageUrl)}
              alt="Preview"
              // Whole image, at its own shape. It was `cover` in a fixed
              // 250x150 box, and posts are portrait or square — so the preview
              // cut the top and bottom off the very thing it was there to let
              // someone check before publishing.
              style={{
                objectFit: "contain",
                borderRadius: "6px",
                maxHeight: "320px",
                maxWidth: "100%",
                height: "auto",
                width: "auto",
              }}
            />
          )}
          {values.video && values.video.uploadedImageUrl && (
            <video controls autoPlay muted>
              <source src={convertToCloudFrontUrl(values.video.uploadedImageUrl)} type="video/mp4" />
            </video>
          )}
        </div>

        <div className="d-flex gap-2 justify-content-end mt-3">
          <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isSubmit}>Cancel</button>
          <Button onClick={handleAddAndUpdateValues} disabled={isSubmit || isUploading || !taxonomyReady} isSubmit={isSubmit} label="Submitting ..." text={isEditPostModalOpen ? "Update" : "Submit"} />
        </div>
      </div>
    </Modal>
    </>
  );
};

export default PostPopup;
